/**
 * Global Data Store - Single Source of Truth for Mock Data
 * 
 * This module ensures data consistency across the application by:
 * 1. Generating all mock data once and caching it
 * 2. Providing a single source of truth for all pages
 * 3. Ensuring Homepage metrics match detail screen data
 */

import { addDays, subDays, isToday, isWithinInterval } from 'date-fns';
import type { PromiseToPay, CollectionCase } from '@/types/collections';
import type { Receipt } from '@/types/payments';

// Mock dispute type for internal use (matches the shape used in generateMockDisputes)
export interface MockDispute {
  id: string;
  customer: string;
  customerId: string;
  customerAccountCode?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  type: string;
  amount: number;
  status: string;
  priority: string;
  assignedTo: string;
  createdDate: Date;
  dueDate: Date;
  requiresResponse: boolean;
  workflow: string;
  aiScore: number;
  aiRecommendation: string;
  currentLevel: number;
  totalLevels: number;
  description: string;
  resolutionNotes: string;
  // Resolution fields
  resolutionOutcome?: 'upheld' | 'denied' | 'partial' | null;
  resolutionAmount?: number | null;
  resolutionDate?: string | null;
  resolvedBy?: string | null;
  resolutionDetails?: string | null;
  resolvedAtLevel?: number | null;
  // Closure fields
  closedReason?: 'duplicate' | 'withdrawn' | 'invalid' | 'other' | null;
  closedDate?: string | null;
  closedNote?: string | null;
  transactions?: Array<{
    id: string;
    referenceNumber: string;
    transactionDate: string;
    dueDate: string;
    amount: number;
    currency: string;
    type: string;
    status: string;
  }>;
}

// Standalone task type
export interface StandaloneTask {
  id: string;
  title: string;
  description: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'open' | 'completed';
  dueDate: Date;
  assignedTo: string;
  customerId?: string;
  customerName?: string;
  amount?: number;
  createdAt: Date;
  updatedAt: Date;
}

// Activity type for case activities
interface CaseActivity {
  type: string;
  description: string;
  timestamp: Date;
  userId?: string;
}

// Import existing generators
import { generateMockPromises, generateMockCases } from './collections-mock-data';
import { generateMockReceipts } from '@/data/payments-mock-data';

/**
 * Global Data Store Singleton
 * Ensures all components use the same data
 */
export class GlobalDataStore {
  // Reset instance to null to force regeneration with updated mock data
  private static instance: GlobalDataStore | null = null;
  
  // Cached data
  private promises: PromiseToPay[] = [];
  private cases: CollectionCase[] = [];
  private receipts: Receipt[] = [];
  private disputes: MockDispute[] = [];
  private standaloneTasks: StandaloneTask[] = [];
  
  private constructor() {
    this.initializeData();
  }
  
  /**
   * Get singleton instance
   */
  public static getInstance(): GlobalDataStore {
    if (!GlobalDataStore.instance) {
      GlobalDataStore.instance = new GlobalDataStore();
    }
    return GlobalDataStore.instance;
  }
  
  /**
   * Reset the store (useful for testing or data refresh)
   */
  public static reset(): void {
    GlobalDataStore.instance = null;
  }
  
  /**
   * Initialize all mock data
   */
  private initializeData(): void {
    // Generate promises
    this.promises = generateMockPromises();
    
    // Generate cases
    this.cases = generateMockCases();
    
    // Generate receipts
    this.receipts = generateMockReceipts();
    
    // Generate disputes
    this.disputes = this.generateMockDisputes();
    
    // Generate standalone tasks
    this.standaloneTasks = this.generateStandaloneTasks();
  }

  /**
   * Process promises to auto-update status based on promise date
   * Any promise with a past promise date and 'Active' status becomes 'Broken'
   */
  private processPromises(promises: PromiseToPay[]): PromiseToPay[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    return promises.map(promise => {
      const promiseDate = new Date(promise.promiseDate);
      promiseDate.setHours(0, 0, 0, 0); // Reset time to start of day

      // If promise date is in the past and status is 'Active', mark as 'Broken'
      if (promiseDate < today && promise.status === 'Active') {
        const daysOverdue = Math.ceil((today.getTime() - promiseDate.getTime()) / (1000 * 60 * 60 * 24));
        return {
          ...promise,
          status: 'Broken' as const,
          daysOverdue,
        };
      }

      return promise;
    });
  }

  /**
   * Process a single promise to auto-update status based on promise date
   */
  private processPromise(promise: PromiseToPay): PromiseToPay {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const promiseDate = new Date(promise.promiseDate);
    promiseDate.setHours(0, 0, 0, 0);

    if (promiseDate < today && promise.status === 'Active') {
      const daysOverdue = Math.ceil((today.getTime() - promiseDate.getTime()) / (1000 * 60 * 60 * 24));
      return {
        ...promise,
        status: 'Broken' as const,
        daysOverdue,
      };
    }

    return promise;
  }

  /**
   * Process cases to auto-update their currentPromise status
   */
  private processCases(cases: CollectionCase[]): CollectionCase[] {
    return cases.map(caseItem => {
      if (caseItem.currentPromise) {
        const processedPromise = this.processPromise(caseItem.currentPromise);
        return {
          ...caseItem,
          currentPromise: processedPromise,
        };
      }
      return caseItem;
    });
  }
  
  /**
   * Generate mock disputes
   */
  private generateMockDisputes(): MockDispute[] {
    return [
      {
        id: "DSP-001",
        customer: "Acme Corporation",
        customerId: "1",
        customerAccountCode: "ACM001",
        contactName: "Robert Anderson",
        contactEmail: "r.anderson@acmecorp.com",
        contactPhone: "+44 20 7123 4567",
        type: "Billing Error",
        amount: 4250.00,
        status: "In Progress",
        priority: "High",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 85,
        aiRecommendation: "Approve partial credit of 30% based on similar cases",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer disputes billing amount for services rendered in December. Claims that agreed-upon discount was not applied to the invoice.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-001",
            referenceNumber: "INV-2024-001",
            transactionDate: "2024-12-15",
            dueDate: "2025-01-15",
            amount: 2500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          },
          {
            id: "TXN-002",
            referenceNumber: "INV-2024-002",
            transactionDate: "2024-12-20",
            dueDate: "2025-01-20",
            amount: 1750.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          }
        ]
      },
      {
        id: "DSP-002",
        customer: "TechStart Inc",
        customerId: "2",
        type: "Service Quality",
        amount: 10500.00,
        status: "Under Review",
        priority: "Critical",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 2),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Escalated Quality Dispute",
        aiScore: 72,
        aiRecommendation: "Escalate to senior management for review",
        currentLevel: 3,
        totalLevels: 4,
        description: "Major quality issues reported with delivered products. Customer requesting full refund and compensation.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-003",
            referenceNumber: "INV-2024-003",
            transactionDate: "2024-12-10",
            dueDate: "2025-01-10",
            amount: 10500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-003",
        customer: "Global Solutions Ltd",
        customerId: "3",
        type: "Payment Dispute",
        amount: 7200.00,
        status: "New",
        priority: "Medium",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 91,
        aiRecommendation: "Verify payment records and contact customer",
        currentLevel: 1,
        totalLevels: 4,
        description: "Customer claims payment was already made but invoice shows outstanding balance.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-004",
            referenceNumber: "INV-2024-004",
            transactionDate: "2024-11-30",
            dueDate: "2024-12-30",
            amount: 7200.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          }
        ]
      },
      {
        id: "DSP-004",
        customer: "Enterprise Systems Co",
        customerId: "4",
        type: "Duplicate Charge",
        amount: 2650.00,
        status: "Resolved",
        priority: "Low",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 10),
        dueDate: subDays(new Date(), 3),
        requiresResponse: false,
        workflow: "Quick Resolution",
        aiScore: 95,
        aiRecommendation: "Issue credit immediately",
        currentLevel: 2,
        totalLevels: 2,
        description: "Duplicate invoice sent to customer. Credit issued and confirmed.",
        resolutionNotes: "Credit memo issued. Customer confirmed receipt and satisfaction.",
        transactions: [
          {
            id: "TXN-005",
            referenceNumber: "INV-2024-005",
            transactionDate: "2024-11-15",
            dueDate: "2024-12-15",
            amount: 2650.00,
            currency: "GBP",
            type: "Invoice",
            status: "Credited"
          },
          {
            id: "TXN-006",
            referenceNumber: "INV-2024-005-DUP",
            transactionDate: "2024-11-15",
            dueDate: "2024-12-15",
            amount: 2650.00,
            currency: "GBP",
            type: "Invoice",
            status: "Credited"
          }
        ]
      },
      {
        id: "DSP-005",
        customer: "Innovate Labs",
        customerId: "5",
        type: "Contract Terms",
        amount: 15750.00,
        status: "Escalated",
        priority: "High",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 5),
        dueDate: addDays(new Date(), 2),
        requiresResponse: false,
        workflow: "Legal Review Required",
        aiScore: 68,
        aiRecommendation: "Consult legal team for contract interpretation",
        currentLevel: 3,
        totalLevels: 4,
        description: "Dispute over contract terms and pricing structure. Requires legal review.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-007",
            referenceNumber: "INV-2024-007",
            transactionDate: "2024-11-01",
            dueDate: "2024-12-01",
            amount: 8500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          },
          {
            id: "TXN-008",
            referenceNumber: "INV-2024-008",
            transactionDate: "2024-11-15",
            dueDate: "2024-12-15",
            amount: 7250.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-006",
        customer: "Digital Dynamics",
        customerId: "6",
        type: "Billing Error",
        amount: 5500.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 88,
        aiRecommendation: "Review invoice and issue correction",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer disputes invoice total. Calculation error suspected.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-009",
            referenceNumber: "INV-2024-009",
            transactionDate: "2024-12-01",
            dueDate: "2025-01-01",
            amount: 5500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-007",
        customer: "Retail Solutions",
        customerId: "7",
        type: "Service Quality",
        amount: 4200.00,
        status: "New",
        priority: "High",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Quality Dispute",
        aiScore: 82,
        aiRecommendation: "Schedule customer call to discuss concerns",
        currentLevel: 1,
        totalLevels: 4,
        description: "Customer reports quality issues with recent delivery.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-010",
            referenceNumber: "INV-2024-010",
            transactionDate: "2024-12-05",
            dueDate: "2025-01-05",
            amount: 4200.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-008",
        customer: "Manufacturing Plus",
        customerId: "8",
        type: "Payment Dispute",
        amount: 7800.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 89,
        aiRecommendation: "Verify payment application",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer claims payment not properly applied to account.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-011",
            referenceNumber: "INV-2024-011",
            transactionDate: "2024-11-20",
            dueDate: "2024-12-20",
            amount: 3900.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          },
          {
            id: "TXN-012",
            referenceNumber: "INV-2024-012",
            transactionDate: "2024-11-25",
            dueDate: "2024-12-25",
            amount: 3900.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          }
        ]
      },
      {
        id: "DSP-009",
        customer: "Tech Innovations",
        customerId: "9",
        type: "Billing Error",
        amount: 5600.00,
        status: "Under Review",
        priority: "High",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 86,
        aiRecommendation: "Review pricing agreement",
        currentLevel: 2,
        totalLevels: 4,
        description: "Pricing discrepancy on recent invoice.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-013",
            referenceNumber: "INV-2024-013",
            transactionDate: "2024-12-08",
            dueDate: "2025-01-08",
            amount: 5600.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-010",
        customer: "Global Traders",
        customerId: "10",
        type: "Contract Terms",
        amount: 9200.00,
        status: "New",
        priority: "Critical",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Legal Review Required",
        aiScore: 75,
        aiRecommendation: "Escalate to legal team",
        currentLevel: 1,
        totalLevels: 4,
        description: "Contract interpretation dispute.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-014",
            referenceNumber: "INV-2024-014",
            transactionDate: "2024-10-15",
            dueDate: "2024-11-15",
            amount: 4600.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          },
          {
            id: "TXN-015",
            referenceNumber: "INV-2024-015",
            transactionDate: "2024-11-15",
            dueDate: "2024-12-15",
            amount: 4600.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-011",
        customer: "Premier Services",
        customerId: "11",
        type: "Duplicate Charge",
        amount: 3800.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Quick Resolution",
        aiScore: 93,
        aiRecommendation: "Issue credit immediately",
        currentLevel: 2,
        totalLevels: 4,
        description: "Duplicate invoice identified by customer.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-016",
            referenceNumber: "INV-2024-016",
            transactionDate: "2024-12-03",
            dueDate: "2025-01-03",
            amount: 3800.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          },
          {
            id: "TXN-017",
            referenceNumber: "INV-2024-016-DUP",
            transactionDate: "2024-12-03",
            dueDate: "2025-01-03",
            amount: 3800.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-012",
        customer: "Logistics Express",
        customerId: "12",
        type: "Service Quality",
        amount: 6400.00,
        status: "Under Review",
        priority: "High",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Escalated Quality Dispute",
        aiScore: 78,
        aiRecommendation: "Arrange site visit",
        currentLevel: 3,
        totalLevels: 4,
        description: "Service delivery issues reported.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-018",
            referenceNumber: "INV-2024-018",
            transactionDate: "2024-11-28",
            dueDate: "2024-12-28",
            amount: 6400.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-013",
        customer: "Construction Co",
        customerId: "13",
        type: "Billing Error",
        amount: 8900.00,
        status: "New",
        priority: "High",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 84,
        aiRecommendation: "Review billing calculations",
        currentLevel: 1,
        totalLevels: 4,
        description: "Billing calculation error suspected.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-019",
            referenceNumber: "INV-2024-019",
            transactionDate: "2024-12-01",
            dueDate: "2024-12-31",
            amount: 8900.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-014",
        customer: "Healthcare Systems",
        customerId: "14",
        type: "Payment Dispute",
        amount: 12300.00,
        status: "In Progress",
        priority: "Critical",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 81,
        aiRecommendation: "Verify payment records",
        currentLevel: 2,
        totalLevels: 4,
        description: "Payment application discrepancy.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-020",
            referenceNumber: "INV-2024-020",
            transactionDate: "2024-11-10",
            dueDate: "2024-12-10",
            amount: 6150.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          },
          {
            id: "TXN-021",
            referenceNumber: "INV-2024-021",
            transactionDate: "2024-11-20",
            dueDate: "2024-12-20",
            amount: 6150.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          }
        ]
      },
      {
        id: "DSP-015",
        customer: "Education Partners",
        customerId: "15",
        type: "Contract Terms",
        amount: 7500.00,
        status: "Under Review",
        priority: "Medium",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Legal Review Required",
        aiScore: 77,
        aiRecommendation: "Review contract terms",
        currentLevel: 2,
        totalLevels: 4,
        description: "Contract terms clarification needed.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-022",
            referenceNumber: "INV-2024-022",
            transactionDate: "2024-10-20",
            dueDate: "2024-11-19",
            amount: 7500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-016",
        customer: "Financial Services Ltd",
        customerId: "16",
        type: "Billing Error",
        amount: 5900.00,
        status: "New",
        priority: "High",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 87,
        aiRecommendation: "Review invoice details",
        currentLevel: 1,
        totalLevels: 4,
        description: "Invoice discrepancy reported.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-023",
            referenceNumber: "INV-2024-023",
            transactionDate: "2024-12-02",
            dueDate: "2025-01-01",
            amount: 5900.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-017",
        customer: "Transport Solutions",
        customerId: "17",
        type: "Service Quality",
        amount: 4700.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Quality Dispute",
        aiScore: 83,
        aiRecommendation: "Schedule quality review",
        currentLevel: 4,
        totalLevels: 4,
        description: "Service quality concerns raised.",
        resolutionNotes: "",
        resolutionOutcome: "upheld",
        resolutionAmount: null,
        resolutionDate: new Date().toISOString(),
        resolvedBy: "John Smith",
        resolutionDetails: "Quality audit confirmed customer's concerns were valid. Service did not meet contractual standards. Full credit issued and service improvements implemented.",
        transactions: [
          {
            id: "TXN-024",
            referenceNumber: "INV-2024-024",
            transactionDate: "2024-11-25",
            dueDate: "2024-12-25",
            amount: 4700.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-018",
        customer: "Energy Corp",
        customerId: "18",
        type: "Payment Dispute",
        amount: 10500.00,
        status: "Under Review",
        priority: "Critical",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 79,
        aiRecommendation: "Verify payment status",
        currentLevel: 4,
        totalLevels: 4,
        description: "Payment verification required.",
        resolutionNotes: "",
        resolutionOutcome: "denied",
        resolutionAmount: null,
        resolutionDate: new Date().toISOString(),
        resolvedBy: "Mike Chen",
        resolutionDetails: "After thorough investigation, payment was confirmed as received. Customer's bank records show successful transaction. Dispute denied.",
        transactions: [
          {
            id: "TXN-025",
            referenceNumber: "INV-2024-025",
            transactionDate: "2024-11-05",
            dueDate: "2024-12-05",
            amount: 10500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Outstanding"
          }
        ]
      },
      {
        id: "DSP-019",
        customer: "Hospitality Group",
        customerId: "19",
        type: "Duplicate Charge",
        amount: 3500.00,
        status: "New",
        priority: "Medium",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Quick Resolution",
        aiScore: 92,
        aiRecommendation: "Issue credit",
        currentLevel: 1,
        totalLevels: 4,
        description: "Duplicate charge identified.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-026",
            referenceNumber: "INV-2024-026",
            transactionDate: "2024-12-04",
            dueDate: "2025-01-03",
            amount: 3500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          },
          {
            id: "TXN-027",
            referenceNumber: "INV-2024-026-DUP",
            transactionDate: "2024-12-04",
            dueDate: "2025-01-03",
            amount: 3500.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-020",
        customer: "Media Networks",
        customerId: "20",
        type: "Billing Error",
        amount: 6800.00,
        status: "In Progress",
        priority: "High",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 85,
        aiRecommendation: "Review billing records",
        currentLevel: 4,
        totalLevels: 4,
        description: "Billing error investigation.",
        resolutionNotes: "",
        resolutionOutcome: "partial",
        resolutionAmount: 2500,
        resolutionDate: new Date().toISOString(),
        resolvedBy: "David Wilson",
        resolutionDetails: "Investigation revealed partial billing error. Customer was overcharged for services not rendered in November. Partial credit of £2,500 issued.",
        transactions: [
          {
            id: "TXN-028",
            referenceNumber: "INV-2024-028",
            transactionDate: "2024-11-30",
            dueDate: "2024-12-30",
            amount: 6800.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
      {
        id: "DSP-021",
        customer: "Consulting Partners",
        customerId: "21",
        type: "Contract Terms",
        amount: 11200.00,
        status: "Under Review",
        priority: "Critical",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Legal Review Required",
        aiScore: 74,
        aiRecommendation: "Legal consultation needed",
        currentLevel: 3,
        totalLevels: 4,
        description: "Contract dispute escalation.",
        resolutionNotes: "",
        transactions: [
          {
            id: "TXN-029",
            referenceNumber: "INV-2024-029",
            transactionDate: "2024-10-10",
            dueDate: "2024-11-09",
            amount: 5600.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          },
          {
            id: "TXN-030",
            referenceNumber: "INV-2024-030",
            transactionDate: "2024-11-10",
            dueDate: "2024-12-10",
            amount: 5600.00,
            currency: "GBP",
            type: "Invoice",
            status: "Disputed"
          }
        ]
      },
    ];
  }
  
  /**
   * Generate standalone tasks (non-customer specific tasks)
   */
  private generateStandaloneTasks(): StandaloneTask[] {
    const today = new Date();
    return [
      {
        id: "TASK-1001",
        title: "Monthly Credit Review",
        description: "Review credit limits and exposure for all high-value customers",
        priority: "High",
        status: "open",
        dueDate: addDays(today, 3),
        assignedTo: "Sarah Johnson",
        createdAt: subDays(today, 5),
        updatedAt: subDays(today, 5),
      },
      {
        id: "TASK-1002",
        title: "Monthly Risk Review",
        description: "Assess portfolio risk and identify potential problem accounts",
        priority: "High",
        status: "open",
        dueDate: addDays(today, 5),
        assignedTo: "John Smith",
        createdAt: subDays(today, 7),
        updatedAt: subDays(today, 7),
      },
      {
        id: "TASK-1003",
        title: "Regular Customer Call: Acme Corporation",
        description: "Quarterly relationship call to discuss payment terms and account status",
        priority: "Medium",
        status: "open",
        dueDate: addDays(today, 2),
        assignedTo: "Sarah Johnson",
        customerId: "1",
        customerName: "Acme Corporation",
        createdAt: subDays(today, 10),
        updatedAt: subDays(today, 10),
      },
      {
        id: "TASK-1004",
        title: "Regular Customer Call: TechStart Inc",
        description: "Monthly check-in call to review outstanding invoices and payment schedule",
        priority: "Medium",
        status: "open",
        dueDate: addDays(today, 1),
        assignedTo: "Mike Chen",
        customerId: "2",
        customerName: "TechStart Inc",
        createdAt: subDays(today, 8),
        updatedAt: subDays(today, 8),
      },
      {
        id: "TASK-1005",
        title: "Quarterly Credit Policy Review",
        description: "Review and update credit policies based on current market conditions",
        priority: "Medium",
        status: "open",
        dueDate: addDays(today, 14),
        assignedTo: "David Wilson",
        createdAt: subDays(today, 3),
        updatedAt: subDays(today, 3),
      },
      {
        id: "TASK-1006",
        title: "Regular Customer Call: Global Solutions Ltd",
        description: "Bi-weekly call to discuss payment plan progress and upcoming invoices",
        priority: "High",
        status: "open",
        dueDate: today,
        assignedTo: "Emily Davis",
        customerId: "3",
        customerName: "Global Solutions Ltd",
        createdAt: subDays(today, 14),
        updatedAt: subDays(today, 14),
      },
      {
        id: "TASK-1007",
        title: "Team Training: New Collection Strategies",
        description: "Conduct training session on updated collection strategies and best practices",
        priority: "Medium",
        status: "open",
        dueDate: addDays(today, 7),
        assignedTo: "Sarah Johnson",
        createdAt: subDays(today, 12),
        updatedAt: subDays(today, 12),
      },
      {
        id: "TASK-1008",
        title: "Regular Customer Call: Enterprise Systems Co",
        description: "Monthly account review and payment terms discussion",
        priority: "Low",
        status: "open",
        dueDate: addDays(today, 4),
        assignedTo: "John Smith",
        customerId: "4",
        customerName: "Enterprise Systems Co",
        createdAt: subDays(today, 6),
        updatedAt: subDays(today, 6),
      },
      {
        id: "TASK-1009",
        title: "Aged Debt Analysis Report",
        description: "Prepare monthly aged debt analysis report for management review",
        priority: "High",
        status: "open",
        dueDate: addDays(today, 2),
        assignedTo: "Mike Chen",
        createdAt: subDays(today, 4),
        updatedAt: subDays(today, 4),
      },
      {
        id: "TASK-1010",
        title: "Regular Customer Call: Innovate Labs",
        description: "Weekly call to monitor payment commitment and address any concerns",
        priority: "High",
        status: "open",
        dueDate: today,
        assignedTo: "David Wilson",
        customerId: "5",
        customerName: "Innovate Labs",
        createdAt: subDays(today, 7),
        updatedAt: subDays(today, 7),
      },
      {
        id: "TASK-1011",
        title: "Credit Insurance Review",
        description: "Review credit insurance coverage and update policies as needed",
        priority: "Medium",
        status: "open",
        dueDate: addDays(today, 10),
        assignedTo: "Emily Davis",
        createdAt: subDays(today, 5),
        updatedAt: subDays(today, 5),
      },
      {
        id: "TASK-1012",
        title: "Regular Customer Call: Digital Dynamics",
        description: "Bi-monthly relationship call to discuss account performance",
        priority: "Low",
        status: "open",
        dueDate: addDays(today, 6),
        assignedTo: "Sarah Johnson",
        customerId: "6",
        customerName: "Digital Dynamics",
        createdAt: subDays(today, 9),
        updatedAt: subDays(today, 9),
      },
    ];
  }
  
  // ============================================
  // PROMISES QUERIES
  // ============================================
  
  /**
   * Get all promises
   */
  public getAllPromises(): PromiseToPay[] {
    return this.processPromises(this.promises);
  }

  /**
   * Get promises (alias for getAllPromises for consistency)
   */
  public getPromises(): PromiseToPay[] {
    return this.getAllPromises();
  }
  
  /**
   * Get promises due today
   */
        public getPromisesDueToday(): PromiseToPay[] {
          const processed = this.processPromises(this.promises);
          return processed.filter(p => {
      const promiseDate = new Date(p.promiseDate);
      return isToday(promiseDate) && p.status === 'Active';
    });
  }
  
  /**
   * Get promises due this week
   */
  public getPromisesDueThisWeek(): PromiseToPay[] {
    const today = new Date();
    const endOfWeek = addDays(today, 7);
    const processed = this.processPromises(this.promises);
    return processed.filter(p => {
      const promiseDate = new Date(p.promiseDate);
      return isWithinInterval(promiseDate, { start: today, end: endOfWeek }) && p.status === 'Active';
    });
  }
  
  /**
   * Get broken promises
   */
  public getBrokenPromises(): PromiseToPay[] {
    const processed = this.processPromises(this.promises);
    return processed.filter(p => p.status === 'Broken');
  }
  
  /**
   * Get promises kept today
   */
  public getPromisesKeptToday(): PromiseToPay[] {
    return this.promises.filter(p => {
      // In a real system, this would check when the promise was fulfilled
      // For mock data, we'll return a subset of 'Paid' promises
      return p.status === 'Paid';
    }).slice(0, 25); // Return first 25 to ensure we have at least 20
  }
  
  // ============================================
  // CASES QUERIES
  // ============================================
  
  /**
   * Get all cases
   */
  public getAllCases(): CollectionCase[] {
    return this.processCases(this.cases);
  }

  /**
   * Get cases (alias for getAllCases for consistency)
   */
  public getCases(): CollectionCase[] {
    return this.getAllCases();
  }
  
  /**
   * Get cases due today
   */
        public getCasesDueToday(): CollectionCase[] {
          const processed = this.processCases(this.cases);
          return processed.filter(c => {
      const actionDate = new Date(c.nextActionDate);
      return isToday(actionDate) && c.status === 'Active';
    });
  }
  
  /**
   * Get cases resolved today
   */
  public getCasesResolvedToday(): CollectionCase[] {
    // In a real system, this would check resolution date
    // For mock data, we'll return a subset of 'Closed' cases
    const processed = this.processCases(this.cases);
    return processed.filter(c => c.status === 'Closed').slice(0, 3);
  }
  
  // ============================================
  // RECEIPTS QUERIES
  // ============================================
  
  /**
   * Get all receipts
   */
  public getAllReceipts(): Receipt[] {
    return this.receipts;
  }
  
  /**
   * Get unallocated receipts
   */
  public getUnallocatedReceipts(): Receipt[] {
    return this.receipts.filter(r => r.unappliedAmount === r.receivedAmount);
  }
  
  /**
   * Get receipts received today
   */
  public getReceiptsReceivedToday(): Receipt[] {
    return this.receipts.filter(r => {
      const receiptDate = new Date(r.receiptDate);
      return isToday(receiptDate);
    });
  }
  
  /**
   * Get partially allocated receipts
   */
  public getPartiallyAllocatedReceipts(): Receipt[] {
    return this.receipts.filter(r => r.unappliedAmount > 0 && r.unappliedAmount < r.receivedAmount);
  }
  
  // ============================================
  // DISPUTES QUERIES
  // ============================================
  
  /**
   * Get all disputes
   */
  public getAllDisputes(): MockDispute[] {
    return this.disputes;
  }

  /**
   * Get disputes (alias for getAllDisputes for consistency)
   */
  public getDisputes(): MockDispute[] {
    return this.getAllDisputes();
  }
  
  /**
   * Get all standalone tasks
   */
  public getAllStandaloneTasks(): StandaloneTask[] {
    return this.standaloneTasks;
  }
  
  /**
   * Get standalone tasks (alias for getAllStandaloneTasks for consistency)
   */
  public getStandaloneTasks(): StandaloneTask[] {
    return this.getAllStandaloneTasks();
  }
  
  /**
   * Get disputes requiring response today
   */
        public getDisputesRequiringResponseToday(): MockDispute[] {
          return this.disputes.filter(d => {
      const dueDate = new Date(d.dueDate);
      return isToday(dueDate) && d.requiresResponse && d.status !== 'Resolved' && d.status !== 'Closed';
    });
  }
  
  /**
   * Get active disputes
   */
  public getActiveDisputes(): MockDispute[] {
    return this.disputes.filter(d => 
      d.status === 'In Progress' || 
      d.status === 'Under Review' || 
      d.status === 'New' ||
      d.status === 'Escalated'
    );
  }

  /**
   * Update a case with new data
   */
  public updateCase(caseId: string, updates: Partial<CollectionCase>): void {
    const caseIndex = this.cases.findIndex(c => c.caseId === caseId);
    if (caseIndex !== -1) {
      this.cases[caseIndex] = {
        ...this.cases[caseIndex],
        ...updates,
      };
    }
  }

  /**
   * Add an activity entry to a case
   */
  public addActivity(caseId: string, activity: CaseActivity): void {
    const caseIndex = this.cases.findIndex(c => c.caseId === caseId);
    if (caseIndex !== -1) {
      // In a real app, this would be stored in a separate activities collection
      // For now, we'll just log it
      console.log(`Activity added to case ${caseId}:`, activity);
    }
  }

  /**
   * Update a dispute with new data
   */
  public updateDispute(disputeId: string, updates: Partial<MockDispute>): void {
    const disputeIndex = this.disputes.findIndex(d => d.id === disputeId);
    if (disputeIndex !== -1) {
      this.disputes[disputeIndex] = {
        ...this.disputes[disputeIndex],
        ...updates,
      };
    }
  }
}

// Export singleton instance getter
export const getGlobalDataStore = () => GlobalDataStore.getInstance();

// Export reset function for testing
export const resetGlobalDataStore = () => GlobalDataStore.reset();
